import { Badge } from "components/UIComponents/Badge";
import { ProgressBar } from "components/UIComponents/ProgressBar";
import { getTranslations } from "features/i18n/translations";
import { useAppSelector } from "lib/redux/hooks";
import { zip } from "ramda";
import { ReactElement, ReactNode } from "react"
import { FormattedNumber } from "react-intl";
import { menuEditorSelectors } from "../editorSlice";
import { getMmCssValue, getShowWineBadges, getShowWineFood, getWineHeightMm, getWineItemShowAppellation, getWineItemShowYear, getWineMarginBottomMm } from "../editorUtils";
import { getMaxWineTextLength, MAX_WINE_NAME_LENGTH } from "../MenuDocumentModel";
import { WinePreviewItemProps } from "./WinePreviewItem"
import { WineText } from "./WineText";


export function LeafletPreviewItem(props: WinePreviewItemProps): ReactElement {
    const document = useAppSelector(menuEditorSelectors.document);
    const wine = props.wineItem.menuItem;
    const isPreview = props.isPreview;
    const hasError = isPreview && (wine.name.length > MAX_WINE_NAME_LENGTH || wine.text.length > getMaxWineTextLength(props.format));

    if (!document) return <></>;

    const translations = getTranslations(document.language || "de");

    const appellation = getWineItemShowAppellation(wine, document)?wine.appellation:"";
    const year = getWineItemShowYear(wine, document)?wine.year:"";
    
    const scale = wine.img_scale ? wine.img_scale : 1.25;

    const imgStyle = {
        '--tw-scale-x': scale,
        '--tw-scale-y': scale,
    };

    const pricePairs = zip(document.sizes, wine.prices??[]).filter(([, price])=>price!==null);

    return <article className={`w-full overflow-hidden
                                flex items-stretch relative`}
        style={{
            height: getMmCssValue(getWineHeightMm(props.format, wine)),
            marginBottom: getMmCssValue(getWineMarginBottomMm(props.format), "0")
        }}
    >
        {wine.title&&<div className={`absolute left-0 top-0
            mt-2 font-barlow text-[14pt] font-medium text-text
            w-[80mm] 
            overflow-hidden break-words
        `} style={{zIndex: 1, lineHeight: "17pt"}}>
            <WineText text={wine.title}/>
        </div>}
        <div className="self-stretch flex-none overflow-hidden flex justify-center"
            style={{
                width: "35mm",
                paddingTop: "12mm",
                paddingBottom: "12mm"
            }}
        >
            {wine.img_url && (
                <img alt={wine.name}
                    className={`transform ${scale} object-cover h-full`}
                    style={imgStyle as any}
                    src={wine.img_url}
                />
            )}
        </div>

        <div className={`py-[.5mm] overflow-hidden flex flex-col 
            justify-center ${hasError && 'bg-[#AF143033] rounded'}
            break-words
            `}
            style={{
                width: "45mm"
            }}
        >
            <div className="flex flex-col gap-2">
                {pricePairs.map(([priceName, price], i) => {
                    price ??= 0;
                    let displayPriceName = priceName.replace("dl", " dl");
                    if (priceName.includes("dl")) {
                        displayPriceName = `${translations["leaflet.template.glass"]} ${displayPriceName}`;
                    }
                    if (priceName === "Flasche") {
                        displayPriceName = translations["size.Flasche"];
                    }
                    
                    return <div className="flex items-baseline text-base text-[10pt]" key={i}>
                        <div>
                            {displayPriceName}
                        </div>
                        <hr className="flex-auto h-px t-2 border-dotted border-t border-text"/>
                        <div>
                            {price>0&&<FormattedNumber
                                minimumFractionDigits={2}
                                maximumFractionDigits={2}
                                value={price}
                            />}
                        </div>
                    </div>;
                })}
            </div>
            {pricePairs.length > 0 && <div className="mt-2">
                <hr className="border-text border-2 border-b" />
            </div>}
            {getShowWineBadges(wine) && <div className="text-[9pt] mt-2 flex gap-2">
                {wine.badges?.map((b, index) => <Badge key={index} type={b}/>) ?? null}
            </div>}
            <h2 className="mt-2 font-barlow text-[14pt] font-medium leading-none text-text">
                {wine.name}
            </h2>
            <div className="mt-2 text-text text-[10pt]">
                {
                    [
                        year,
                        appellation,
                        [
                            wine.country,
                            wine.region
                        ].filter(Boolean).join(', '),
                        wine.producer,
                        wine.grapes,
                        getShowWineFood(wine)&&
                            <>
                                {translations["editor.wine.suitableFood"]}: {wine.suitableFood}
                            </>
                    ].filter(Boolean).map((s, i) => <div className="flex flex-col" key={i}>
                        <div className="leading-none overflow-hidden max-h-[20pt] mt-[2pt] pb-[2pt]">{s}</div>
                        <hr className="flex-auto h-px t-2 border-dotted border-t border-text my-0.5"/>
                    </div>)
                }
            </div>
            {(wine.showTasteSweetness == null || wine.showTasteSweetness)&&
                <div className="mt-1 flex flex-col text-text text-[10pt]">
                    <LeafletBar 
                        label={translations["editor.wine.taste"]}
                        value={wine.taste}
                    />
                    <LeafletBar 
                        label={translations["editor.wine.sweetness"]}
                        value={wine.sweetness}
                    />
                </div>
            }
            <div className="mt-2 leading-none italic text-text text-[9pt] 
                overflow-hidden max-h-[64pt] pb-[1pt]
                ">
                <WineText text={wine.text}/>
            </div>
        </div>
    </article>
}


function LeafletBar({label, value}: { label: ReactNode, value: number | null | undefined }): ReactElement | null {
    if (value == null) {
        return null;
    }

    return (
        <div className="flex items-center gap-2 justify-between pt-px">
            <span className="leading-tight whitespace-nowrap font-bold">{label}</span>
            <div style={{
                width: "45%",
                height: "1.3mm"
            }}>
                <ProgressBar fillPercent={value * 10}/>
            </div>
        </div>
    );
}
